import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { ResourcesHeader } from "@/components/ResourcesHeader";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { useI18n } from "@/i18n";
import { useSEO, SEO_BASE_URL } from "@/hooks/useSEO";
import { getArticle } from "@/data/articles";

export default function Article() {
  const { slug } = useParams();
  const { lang } = useI18n();
  const article = getArticle(slug);

  const a = article ? article[lang] : null;
  const label = lang === "it" ? "Risorse" : "Resources";
  const url = `${SEO_BASE_URL}/risorse/${slug}`;

  useSEO({
    title: a ? `${a.title} | JetSeat` : undefined,
    description: a ? a.description : undefined,
    canonical: article ? `/risorse/${slug}` : undefined,
    jsonLd: article ? {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SEO_BASE_URL}/` },
            { "@type": "ListItem", "position": 2, "name": label, "item": `${SEO_BASE_URL}/risorse` },
            { "@type": "ListItem", "position": 3, "name": a.title, "item": url },
          ],
        },
        {
          "@type": "Article",
          "headline": a.title,
          "description": a.description,
          "image": article.image,
          "inLanguage": lang === "it" ? "it-IT" : "en-US",
          "author": { "@type": "Organization", "name": "JetSeat" },
          "publisher": { "@type": "Organization", "name": "JetSeat", "logo": { "@type": "ImageObject", "url": `${SEO_BASE_URL}/jetseat-logo.png` } },
          "mainEntityOfPage": { "@type": "WebPage", "@id": url },
        },
      ],
    } : undefined,
  });

  if (!article) return <Navigate to="/risorse" replace />;


  const cta = lang === "it"
    ? { t: "Vuoi un programma su misura per la tua azienda?", b: "Parliamone", }
    : { t: "Want a program tailored to your company?", b: "Let's talk" };

  return (
    <div className="bg-background min-h-screen" data-testid="article-page">
      <ResourcesHeader />
      <main>
        <article className="max-w-3xl mx-auto px-6 md:px-8 py-16 md:py-24">
          <Reveal>
            <nav className="text-sm text-muted-foreground mb-8" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-foreground">Home</Link> <span className="mx-2">/</span>
              <Link to="/risorse" className="hover:text-foreground">{label}</Link> <span className="mx-2">/</span>
              <span className="text-foreground">{a.title}</span>
            </nav>
            <p className="overline text-accent">{label}</p>
            <h1 className="mt-4 font-serif text-4xl md:text-6xl tracking-tight leading-[1.03]">{a.title}</h1>
            <p className="mt-5 text-muted-foreground text-sm">{a.readingTime}</p>
          </Reveal>

          <Reveal delay={0.05}>
            <div className="mt-10 overflow-hidden border border-border">
              <img src={article.image} alt={a.title} className="w-full h-[380px] object-cover" />
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <p className="mt-10 font-serif italic text-2xl text-foreground/90 leading-snug">{a.excerpt}</p>
          </Reveal>

          <div className="mt-8 space-y-10">
            {a.sections.map((s, i) => (
              <Reveal key={i} delay={0.05}>
                <section>
                  <h2 className="font-serif text-2xl md:text-3xl tracking-tight">{s.h}</h2>
                  <p className="mt-4 text-muted-foreground text-lg leading-relaxed">{s.p}</p>
                </section>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.05}>
            <div className="mt-16 border border-border bg-card p-8 md:p-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <p className="font-serif text-2xl tracking-tight max-w-md">{cta.t}</p>
              <Link to="/#book" data-testid="article-cta" className="inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-midnight hover:scale-[1.03] transition-transform duration-300 whitespace-nowrap">
                {cta.b} <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
        </article>
      </main>
      <Footer />
    </div>
  );
}
